import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';

import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';
import {isSessionExpiredError} from '../../../utils/authErrors';

import RegisterRobotModal from './RegisterRobotModal';
import UnregisterRobotModal from './UnregisterRobotModal';
import OperatorApplicationNewEmailModal from './OperatorApplicationNewEmailModal';
import Tooltip from '../../Tooltip/Tooltip';

import accountStyles from '../Account.module.css';
import styles from './OperatorSection.module.css';

import checkCircleBlue from '../../../static_files/check-circle-blue.svg';
import checkCircle from '../../../static_files/check-circle-1.svg';
import ellipsisGray from '../../../static_files/ellipsis-gray.svg';
import plusWhite from '../../../static_files/plus-white.svg';
// import settingsWhite from '../../../static_files/settings-white.svg';
import trashCanRed from '../../../static_files/trash-can-red.svg';


const operatorFeatures = [
    'Operate robots to complete real-world tasks.',
    'Contribute high-quality data to earn USD.',
    'Boost Prisma earnings with a 5x ultra-speed multiplier.'
];


const RobotCard = ({ robotData, onRefreshData }) => {
    const isOnline = robotData.status === 'online';
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUnregisterModalOpen, setIsUnregisterModalOpen] = useState(false);
    const [isSubmittingDefaultProducer, setIsSubmittingDefaultProducer] = useState(false);
    const menuRef = useRef(null);

    const { userId, gatewayToken, showNotification, handleUserSessionExpired } = useContext(SwapMainAppContext);

    // Close menu when clicking outside the card menu area
    useEffect(() => {
        if (!isMenuOpen) return;
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMenuOpen]);

    const handleSetDefault = async () => {
        setIsSubmittingDefaultProducer(true);
        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/operator/set-default-producer`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${gatewayToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: userId, machine_id: robotData.machine_id }),
            });
            const result = await response.json();
            if (result.success) {
                await onRefreshData();
                showNotification('success', result.msg);
            } else {
                if (isSessionExpiredError(result.msg)) {
                    handleUserSessionExpired?.();
                    return;
                }
                showNotification('error', result.msg || 'Failed to update default producer.');
            }
        } catch (err) {
            showNotification('error', 'Failed to update default producer.');
        } finally {
            setIsSubmittingDefaultProducer(false);
        }
    };

    return (
        <div className={styles.robotCard}>
            <div className={styles.robotCardHeader}>
                <h4 className={styles.robotName}>{robotData.model}</h4>
                <div className={styles.robotCardMenuWrapper} ref={menuRef}>
                    <button
                        className={styles.robotCardMenuButton}
                        onClick={() => setIsMenuOpen((prev) => !prev)}
                    >
                        <img src={ellipsisGray} alt='ellipsis' />
                    </button>
                    {isMenuOpen && (
                        <div className={styles.robotCardMenuTooltip}>
                            <button
                                className={`${styles.tooltipButton} ${styles.tooltipButtonDanger}`}
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    setIsUnregisterModalOpen(true);
                                }}
                            >
                                <img src={trashCanRed} alt='trash' className={styles.tooltipButtonIcon} />
                                Unregister Robot
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.robotDetails}>
                <div className={styles.robotDetailItem}>
                    <p className={styles.robotDetailLabel}>Manufacturer</p>
                    <p className={styles.robotDetailValue}>{robotData.manufacturer}</p>
                </div>
                {/* <div className={styles.robotDetailItem}>
                    <p className={styles.robotDetailLabel}>Model</p>
                    <p className={styles.robotDetailValue}>{robotData.model}</p>
                </div> */}
                <div className={styles.robotDetailItem}>
                    <p className={styles.robotDetailLabel}>Serial</p>
                    <p className={styles.robotDetailValue}>{robotData.serial_number}</p>
                </div>
                <div className={styles.robotDetailItem}>
                    <p className={styles.robotDetailLabel}>Status</p>
                    <p className={`${styles.robotDetailValue} ${styles.statusRow}`}>
                        <span className={`${styles.statusDot} ${isOnline ? styles.statusDotOnline : styles.statusDotOffline}`} />
                        {isOnline ? 'Online' : 'Offline'}
                    </p>
                </div>
            </div>

            <div className={styles.robotCardSpacer} />

            {robotData.is_default_producer ? (
                <p className={styles.defaultProducerText}>
                    This robot is currently the default data producer.
                </p>
            ) : (
                <button
                    className={styles.defaultProducerButton}
                    onClick={handleSetDefault}
                    disabled={isSubmittingDefaultProducer}
                >
                    {isSubmittingDefaultProducer ? 'Submitting...' : 'Set as Default Producer'}
                </button>
            )}

            {isUnregisterModalOpen && (
                <UnregisterRobotModal
                    robotData={robotData}
                    onClose={() => setIsUnregisterModalOpen(false)}
                    onRefreshData={onRefreshData}
                />
            )}
        </div>
    );
};

const OperatorSection = ({ onUpdateUserProfileEmail, userProfileEmail, autoOpenRegisterModal = false }) => {
    const [robots, setRobots] = useState([]);
    const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
    const [isApplicationNewEmailModalOpen, setIsApplicationNewEmailModalOpen] = useState(false);
    const [operatorStatus, setOperatorStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmittingApplication, setIsSubmittingApplication] = useState(false);
    const hasAutoOpenedRegisterModalRef = useRef(false);

    const {
        userId,
        gatewayToken,
        showNotification,
        userEmail,
        setShouldRefreshVLADataCache,
        handleUserSessionExpired,
    } = useContext(SwapMainAppContext);

    const fetchUserData = async () => {
        setIsLoading(true);
        try {
            if (!gatewayToken) {
                setRobots([]);
                setOperatorStatus(null);
                return;
            }
            const response = await fetch(
                `${PRISMAX_BACKEND_URL}/api/operator/get-user-data?user_id=${userId}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${gatewayToken}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            const result = await response.json();
            if (result.success) {
                // Status is hardcoded as online for now
                setRobots(result.data.robots.map((r) => ({ ...r, status: 'online' })));
                setOperatorStatus(result.data.operator_status);
            } else {
                if (isSessionExpiredError(result.msg)) {
                    handleUserSessionExpired?.();
                    return;
                }
                showNotification('error', result.msg || 'Failed to load user data.');
            }
        } catch (err) {
            showNotification('error', 'Failed to load user data.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, [userId]);

    // Keep application data in sync after an update 
    const handleRefreshData = async () => {
        setShouldRefreshVLADataCache(true); // Trigger a refresh for the VLA data cache, which is on a separate page
        await fetchUserData();
    }

    const handleOpenModalOrSubmitApplication = async () => {
        if (!gatewayToken) {
            showNotification('warning', 'Please log in first before requesting operator membership.');
            return;
        }
        const email = userEmail || userProfileEmail || ''; // Prioritize app context email, then account user profile email
        if (email) {
            // Email already exists — submit directly without opening the modal
            await handleSubmitOperatorApplication(email);
        } else {
            // No email exists — open modal so user can add and verify one first
            setIsApplicationNewEmailModalOpen(true);
        }
    };

    const handleSubmitOperatorApplication = async (email) => {
        setIsSubmittingApplication(true);
        try {
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/operator/submit-membership-application`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${gatewayToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ user_id: userId, email }),
            });
            const result = await response.json();
            if (result.success) {
                showNotification('success', `Thanks for applying to be an Operator. We'll email you at ${result.email} once our team has finished reviewing your application.`, 10000);
                setOperatorStatus('pending');
            } else {
                if (isSessionExpiredError(result.msg)) {
                    handleUserSessionExpired?.();
                    return;
                }
                showNotification('error', result.msg || 'Failed to submit application. Please try again.');
            }
        } catch (err) {
            showNotification('error', 'Failed to submit application. Please try again.');
        } finally {
            setIsSubmittingApplication(false);
        }
    };

    const handleRegisterNewRobot = useCallback(() => {
        if (!gatewayToken) {
            showNotification('warning', 'Please log in first to register a new robot');
            return;
        }
        setIsRegisterModalOpen(true);
    }, [gatewayToken, showNotification]);

    useEffect(() => {
        if (!autoOpenRegisterModal || hasAutoOpenedRegisterModalRef.current || isLoading) return;
        if (!gatewayToken) return;
        hasAutoOpenedRegisterModalRef.current = true;
        handleRegisterNewRobot();
    }, [autoOpenRegisterModal, isLoading, gatewayToken, handleRegisterNewRobot]);

    const renderOperatorStatusOrApplyButton = () => {
        if (isLoading) {
            return null;
        }
        if (operatorStatus === 'approved') {
            return (
                <span className={accountStyles.currentBadge} style={{ borderColor: '#48AEFF' }}>
                    Current membership
                </span>
            );
        }
        if (operatorStatus === 'pending') {
            return (
                <span className={accountStyles.currentBadge}>
                    Membership pending
                </span>
            );
        }
        if (operatorStatus === 'denied') {
            return (
                <Tooltip
                    text={
                        <div style={{ lineHeight: '110%' }}>
                            <span>Your application was declined.<br /> Please contact support@prismax.ai <br /> if you think this was done in <br /> error and would like us to <br /> re-review your application.</span>
                        </div>
                    }
                >
                    <span className={accountStyles.currentBadge}  style={{ cursor: 'pointer' }}>
                        Membership denied
                    </span>
                </Tooltip>
            );
        }
        // operatorStatus === null — not yet applied
        const isApplyButtonDisabled = robots.length == 0;
        if (isApplyButtonDisabled) {
            return (
                <Tooltip
                    text={
                        <div style={{ lineHeight: '110%' }}>
                            <span>Register a robot in order to be <br /> eligible for Operator membership.</span>
                        </div>
                    }
                >
                    <button className={styles.applyButton} disabled>
                        Apply
                    </button>
                </Tooltip>
            );
        }
        return (
            <button className={styles.applyButton} onClick={handleOpenModalOrSubmitApplication} disabled={isSubmittingApplication}>
                {isSubmittingApplication ? 'Applying...' : 'Apply'}
            </button>
        );
    };

    return (
        <>
            <div className={`${styles.operatorBox}`}>
                <div className={styles.featuresSection}>
                    <div>
                        <h3 className={styles.operatorTitle}>Operator</h3>

                        <ul className={styles.operatorPerks}>
                            {operatorFeatures.map((feature, index) => (
                                <li key={index}>
                                    <img src={operatorStatus === 'approved' ? checkCircleBlue : checkCircle} alt='✓' />
                                    {feature}
                                </li>
                            ))}
                        </ul>
                    </div>
                    {renderOperatorStatusOrApplyButton()}
                </div>

                <div className={styles.robotFleetSection}>
                    <h3 className={styles.robotFleetTitle}>My Robot Fleet</h3>

                    <div className={styles.robotGrid}>
                        <button
                            className={styles.registerCard}
                            onClick={handleRegisterNewRobot}
                        >
                            <div className={styles.registerCardInner}>
                                <img className={styles.registerIcon} src={plusWhite} alt="Register robot plus icon" />
                                <span className={styles.registerLabel}>Register New Robot</span>
                            </div>
                        </button>

                        {robots.map((robot) => (
                            <RobotCard
                                key={robot.machine_id}
                                robotData={robot}
                                onRefreshData={handleRefreshData}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {isRegisterModalOpen && (
                <RegisterRobotModal
                    onClose={() => setIsRegisterModalOpen(false)}
                    onRefreshData={handleRefreshData}
                />
            )}

            {isApplicationNewEmailModalOpen && (
                <OperatorApplicationNewEmailModal
                    onClose={() => setIsApplicationNewEmailModalOpen(false)}
                    onUpdateUserProfileEmail={onUpdateUserProfileEmail}
                    onSubmit={handleSubmitOperatorApplication}
                />
            )}
        </>
    );
};

export default OperatorSection;
